import { useState, useEffect, useMemo } from 'react';
import clsx from 'clsx';
import { IHarborData } from '@/types/harbor-management.types';
import { useIntl } from 'react-intl';

interface BusinessHoursProps {
  businessHours?: string;
  setFormData: React.Dispatch<React.SetStateAction<Partial<IHarborData>>>;
  errors: { [key: string]: string };
  setErrors: React.Dispatch<React.SetStateAction<{ [key: string]: string }>>;
  disabled: boolean;
}

interface DayHours {
  open?: string;
  close?: string;
}

const BusinessHours: React.FC<BusinessHoursProps> = ({
  businessHours,
  setFormData,
  errors,
  setErrors,
  disabled,
}) => {
  const intl = useIntl();
  const daysOfWeek = [
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
    'sunday',
  ];

  const initialHours: Record<string, DayHours> = useMemo(() => ({}), []);

  const [hours, setHours] = useState<Record<string, DayHours>>(
    typeof businessHours === 'string'
      ? JSON.parse(businessHours)
      : businessHours || initialHours
  );

  useEffect(() => {
    if (businessHours) {
      try {
        const parsedHours =
          typeof businessHours === 'string'
            ? JSON.parse(businessHours)
            : businessHours;

        const validHours = Object.keys(parsedHours).reduce(
          (acc, day) => {
            if (parsedHours[day].open && parsedHours[day].close) {
              acc[day] = parsedHours[day];
            }
            return acc;
          },
          {} as Record<string, DayHours>
        );

        setHours(validHours);
      } catch (e) {
        console.error('Error parsing business hours:', e);
        setHours(initialHours);
      }
    }
  }, [businessHours, initialHours]);

  const handleToggleDay = (day: string, isOpen: boolean) => {
    let updatedHours: Record<string, DayHours>;
    if (isOpen) {
      updatedHours = {
        ...hours,
        [day]: { open: '08:00', close: '18:00' },
      };
    } else {
      updatedHours = { ...hours };
      delete updatedHours[day]; // Remove the day if closed
    }
    setHours(updatedHours);
    setFormData((prev) => ({
      ...prev,
      businessHours: JSON.stringify(updatedHours),
    }));
    setErrors((prev) => ({ ...prev, businessHours: '' }));
  };

  const handleTimeChange = (
    day: string,
    field: 'open' | 'close',
    value: string
  ) => {
    const updatedHours = {
      ...hours,
      [day]: {
        ...hours[day],
        [field]: value,
      },
    };
    setHours(updatedHours);
    setFormData((prev) => ({
      ...prev,
      businessHours: JSON.stringify(updatedHours),
    }));
    setErrors((prev) => ({ ...prev, businessHours: '' }));
  };

  return (
    <div className="space-y-4">
      {daysOfWeek.map((day) => {
        const isOpen =
          day in hours && 'open' in hours[day] && 'close' in hours[day];
        const dayTranslationKey = `POI.OPENING_HOURS.${day.toUpperCase()}`;
        const dayName = intl.formatMessage({ id: dayTranslationKey });

        return (
          <div key={day} className="flex flex-col gap-2">
            <div className="flex items-center gap-3 ">
              <input
                id={`toggle-${day}`}
                type="checkbox"
                checked={isOpen}
                onChange={(e) => handleToggleDay(day, e.target.checked)}
                disabled={disabled}
                className={clsx(
                  'h-5 w-5 text-blue-500 border-gray-200 rounded focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer',
                  disabled && 'bg-gray-100 cursor-not-allowed'
                )}
              />
              <label
                htmlFor={`toggle-${day}`}
                className="flex items-center gap-3 cursor-pointer"
              >
                <span className="text-sm font-medium text-gray-700 w-24 contrast:!text-white">
                  {dayName}
                </span>
                <span className="text-gray-700 contrast:!text-white">
                  {isOpen
                    ? intl.formatMessage({ id: 'POI.OPENING_HOURS.OPEN' })
                    : intl.formatMessage({ id: 'POI.OPENING_HOURS.CLOSED' })}
                </span>
              </label>
            </div>
            {isOpen && (
              <div className="flex items-center gap-3 ml-28">
                <label htmlFor={`open-time-${day}`} className="sr-only">
                  {`${dayName} opening time`}
                </label>
                <input
                  id={`open-time-${day}`}
                  type="time"
                  value={hours[day].open || ''}
                  onChange={(e) =>
                    handleTimeChange(day, 'open', e.target.value)
                  }
                  disabled={disabled}
                  className={clsx(
                    'input input-bordered w-32 h-10 rounded-lg border-gray-300 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500',
                    disabled && 'bg-gray-100 cursor-not-allowed',
                    errors.businessHours && 'border-red-500'
                  )}
                  autoComplete="off"
                />
                <span className="text-gray-700">
                  {intl.formatMessage({ id: 'POI.OPENING_HOURS.TO' })}
                </span>
                <label htmlFor={`close-time-${day}`} className="sr-only">
                  {`${dayName} closing time`}
                </label>
                <input
                  id={`close-time-${day}`}
                  type="time"
                  value={hours[day].close || ''}
                  onChange={(e) =>
                    handleTimeChange(day, 'close', e.target.value)
                  }
                  disabled={disabled}
                  className={clsx(
                    'input input-bordered w-32 h-10 rounded-lg border-gray-300 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500',
                    disabled && 'bg-gray-100 cursor-not-allowed',
                    errors.businessHours &&
                      'border-red-500 contrast:border-[#FF8B33]'
                  )}
                  autoComplete="off"
                />
              </div>
            )}
          </div>
        );
      })}
      {errors.businessHours && (
        <p
          className="text-red-500 text-xs mt-1 contrast:text-[#FF8B33]"
          role="alert"
          id="businessHours-error"
        >
          {errors.businessHours}
        </p>
      )}
    </div>
  );
};

export default BusinessHours;
